import { CaretLeftIcon, CaretRightIcon, EngineIcon, PlantIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';

import { CloseButton } from '@/components/common/close-button';

import { cropLabel, deviceCountLabel, fieldSubtitle } from '../format';
import type { MapField } from '../schemas';

import styles from './field-details-panel.module.scss';

/**
 * The panel's nominal size, the input `placeMapPanel` needs before layout: 384 wide and 171 tall
 * (Figma `787:678824`) — 12px of padding twice, the 83px content block, and ticket 10's actions row
 * 24px below it (`787:678799`, 40 tall).
 *
 * ponytail: nominal, not measured. A Field name long enough to wrap makes the real panel a line
 * taller, which shifts the placement by that much. Measure the rendered element and feed it back if a
 * wrapped name is ever seen to place the panel visibly wrong.
 */
export const FIELD_PANEL_SIZE = { width: 384, height: 171 } as const;

export type FieldDetailsPanelProps = {
  /**
   * The Field, taken from the **list** payload already in the query cache — not from
   * `GET /fields/tracking/{fieldId}`. Every value this panel renders is on the list row, and the
   * detail route adds only `location` and `devices[]`, which it does not show. So opening a panel
   * costs no request, has no loading state and cannot fail.
   */
  field: MapField;
  onPrevious: () => void;
  onNext: () => void;
  onClose: () => void;
  /**
   * The `Delete Field` / `Edit Field` row (Figma `787:678799`). A slot, not a component: this panel
   * stays ignorant of what the actions do, which keeps the delete mutation, its confirmation and its
   * toast out of here. Ticket 10 fills it with `<FieldPanelActions>`. `undefined` renders nothing.
   */
  actions?: ReactNode;
};

/**
 * The focused Field's details (Figma `787:678824`, leaves `787:678782`–`787:678798`).
 *
 * **Both stepping controls are always enabled** (113586 §4l): traversal circles, so there is no end
 * to disable at. Which Field is next is the caller's — this component only reports the press.
 *
 * The close control has **no Figma frame**: `787:678824` draws only the two carets, yet §4h requires
 * one. Grilling Q11 governs where the design is silent — build from the shipped component library —
 * so it is the same `<CloseButton>` the overlays use.
 */
export function FieldDetailsPanel({
  field,
  onPrevious,
  onNext,
  onClose,
  actions,
}: FieldDetailsPanelProps) {
  const crop = cropLabel(field.crop, field.cropYear);

  return (
    <>
      <div className={styles.header}>
        <p className={styles.title}>{field.name}</p>
        <div className={styles.controls}>
          <button
            type="button"
            className={styles.step}
            aria-label="Previous field"
            onClick={onPrevious}
          >
            <CaretLeftIcon size={20} />
          </button>
          <button type="button" className={styles.step} aria-label="Next field" onClick={onNext}>
            <CaretRightIcon size={20} />
          </button>
          <CloseButton aria-label="Close field details" onClick={onClose} />
        </div>
      </div>

      <p className={styles.subtitle}>{fieldSubtitle(field)}</p>

      {/* A list, not a row of divs: the chips are a set of facts about the Field, and a Crop-less
          Field simply has one fewer — never an empty chip (grilling Q11). */}
      <ul className={styles.chips}>
        <li className={styles.chip}>
          <EngineIcon size={16} aria-hidden />
          {deviceCountLabel(field.deviceCount)}
        </li>
        {crop ? (
          <li className={styles.chip}>
            <PlantIcon size={16} aria-hidden />
            {crop}
          </li>
        ) : null}
      </ul>

      {actions ? <div className={styles.actions}>{actions}</div> : null}
    </>
  );
}
